import PolicyStore from "../../../../stores/policy-store";
import { FormFieldLine } from "../../../../../../components/form-field-line/form-field-line";
import { Toggle } from "../../../../../../components/inputs/toggle/toggle";
import { SelectDropdown } from "../../../../../../components/inputs/select-dropdown/select-dropdown";
import { CONDITION_GROUP_LEVELS } from "../../../../policy-types";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import ConditionBuilder from "./condition-builder/condition-builder";
import FontBody1 from "../../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import { observer } from "mobx-react";
import { Button } from "../../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../../components/inputs/button-variants";
import IconPlus from "../../../../../../components/icons/icon-plus/icon-plus";
import GridLine, { BackgroundColor } from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineItem from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import GridLineButtons from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-buttons/grid-line-buttons";
import { useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { ExpandCallapseStore } from "../../../../../../hooks/expand-collapse-hook";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import TranslationTextarea from "../../../../../../translation/translation-textarea/translation-textarea";

interface IProps {
    policy: PolicyStore;
    level: CONDITION_GROUP_LEVELS,
}

const ConditionGroupCard: React.FC<IProps> = observer(({ policy, level }) => {
    const conditionGroupList = policy.getConditionGroupListByLevel(level);

    const CONTENT_MAP = {
        [CONDITION_GROUP_LEVELS.ORDER]: {
            label: 'Limit Order Eligibility',
            title: 'Orders',
        },
        [CONDITION_GROUP_LEVELS.CUSTOMER]: {
            label: 'Limit Customer Eligibility',
            title: 'Customers',
        },
        [CONDITION_GROUP_LEVELS.RMA]: {
            label: 'Limit RMA Eligibility',
            title: 'RMAs',
        },
        [CONDITION_GROUP_LEVELS.ITEM]: {
            label: 'Limit Item Eligibility',
            title: 'Items',
        },
    }

    const isLimitEligibleItems = policy.getIsLimitEligibleItemsByLevel(level);
    const expandCollapseStore = useOutletContext<ExpandCallapseStore>();

    /** expanding grid-line only if manually switched group to ON */
    useEffect(() => {
        if (isLimitEligibleItems) {
            expandCollapseStore.setExpanded(level, true);
        }
    }, [isLimitEligibleItems, expandCollapseStore, level]);

    /** collapsing grid-line only if group was deleted - no group */
    useEffect(() => {
        if (!conditionGroupList.length) {
            expandCollapseStore.setExpanded(level, false);
        }

    }, [conditionGroupList.length, expandCollapseStore, level]);

    const conditionsSettings: () => React.ReactNode | undefined = () => {
        if (conditionGroupList.length === 0) {
            return undefined;
        }
        const itemLevelCustomReasonField =
            level === CONDITION_GROUP_LEVELS.ITEM ? (
                <FormFieldLine
                    label="Custom ineligibility message"
                    note="Shown to shoppers when this policy blocks an item on the first portal page."
                >
                    <TranslationTextarea
                        translationKey={policy.itemIneligibilityReasonKey}
                        maxLength={200}
                        showCharactersCount
                    />
                </FormFieldLine>
            ) : null;

        return (
            <>
                {itemLevelCustomReasonField}
                <br />
                {conditionGroupList.map((conditionGroup, index) => (
                    <div key={index}>
                        <ListLayout>
                            <FormFieldLine
                                label={conditionGroup.eligibilityDescription}
                            >
                                <SelectDropdown
                                    value={conditionGroup.eligibilityOptions
                                        .filter(o => o.value === conditionGroup.isEligible)
                                        .map(o => ({
                                            value: Number(o.value),
                                            label: o.label
                                        }))[0]}
                                    options={conditionGroup.eligibilityOptions.map(o => ({
                                        value: Number(o.value),
                                        label: o.label
                                    }))}
                                    onChange={(value) => conditionGroup.setIsEligible(Boolean(value?.value))}
                                    isSearchable={false}
                                />
                            </FormFieldLine>
                            <div className="condition-list">
                                <ListLayout>
                                    {conditionGroup.policyConditions.map((condition, index) => (
                                        <ConditionBuilder
                                            key={index}
                                            conditionIndex={index}
                                            conditionGroup={conditionGroup}
                                            condition={condition}
                                        />
                                    ))}
                                </ListLayout>
                            </div>
                            <div className="add-condition-button">
                                <Button
                                    variant={BUTTON_VARIANTS.Text}
                                    text="Add new condition"
                                    leftIcon={IconPlus}
                                    onClick={conditionGroup.addCondition}
                                />
                            </div>
                        </ListLayout>
                    </div>
                ))}
            </>
        );
    }

    return (
        <GridLine
            backgroudColor={BackgroundColor.NEUTRAL6}
            settings={{ columns: [{ width: 2 }, { width: 40, type: 'px' }] }}
            columns={[
                <GridLineItem>
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        {CONTENT_MAP[level].title}
                        <FontBody2 color={FONT_COLOR.PRIMARY2}>  - {CONTENT_MAP[level].label}</FontBody2>
                    </FontBody1>
                </GridLineItem>,
                <GridLineButtons>
                    <Toggle
                        label=''
                        checked={isLimitEligibleItems}
                        onChange={(value) => policy.setLimitEligibleItemsByLevel(value, level)}
                    />
                </GridLineButtons>,
            ]}
            isExpandable={true}
            isExpandDisabled={conditionGroupList.length === 0}
            isExpanded={expandCollapseStore.getIsExpanded(level)}
            expandCallback={(v: boolean) => expandCollapseStore.setExpanded(level, v)}
            expandableContent={conditionsSettings()}
            onClick={() => {}}
        />
    );
});

export default ConditionGroupCard;
